import 'amazon-connect-streams';
import { useEffect, useRef } from 'react';
import { useAppContext } from '../../../AppContext';
import { useChatContext } from '../../../contexts/ChatContext';
import { useEmailContext } from '../../../contexts/EmailContext';
import { useTelephonyContext } from '../../../contexts/TelephonyContext';
import { Page } from '../../../models/Page';
import { CloseContext } from '../../CassieContainer/websocket/types';

interface UseChatLifecycleProps {
  selectedChatRef: React.MutableRefObject<string | null>;
  chatContactsRef: React.MutableRefObject<string[]>;
  onViewContact?: (contactId: string | undefined, closeContext?: CloseContext) => void;
}

export function useChatLifecycle({ selectedChatRef, chatContactsRef, onViewContact }: UseChatLifecycleProps): void {
  const { activePage, setActivePage } = useAppContext();
  const { selectedChat, setSelectedChat, setChatContacts, setPreviousChatMessagesByContact } = useChatContext();
  const { voiceContact } = useTelephonyContext();
  const { emailContacts } = useEmailContext();

  const activePageRef = useRef(activePage);
  activePageRef.current = activePage;

  const emailContactsRef = useRef(emailContacts);
  emailContactsRef.current = emailContacts;

  const voiceContactRef = useRef(voiceContact);
  voiceContactRef.current = voiceContact;

  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (isInitializedRef.current) {
      return;
    }
    isInitializedRef.current = true;

    connect.contact(contact => {
      if (contact.getType() !== connect.ContactType.CHAT) {
        return;
      }

      contact.onConnected(() => {
        const currentContacts = chatContactsRef.current;
        const isFirstChat = currentContacts?.length === 1;
        const isHandlingOtherContacts = emailContactsRef.current.length > 0 || voiceContactRef.current !== null;
        const isFirstContactOverview = isFirstChat && !isHandlingOtherContacts;

        if (isFirstContactOverview && !selectedChat && activePageRef.current === Page.CHAT) {
          setSelectedChat(currentContacts[0]);
        }
      });

      contact.onConnecting(() => {
        setActivePage(Page.CHAT);

        setChatContacts(prevContacts => {
          if (!prevContacts.includes(contact.contactId)) {
            return [...prevContacts, contact.contactId];
          }
          return prevContacts;
        });
      });

      contact.onDestroy(() => {
        setChatContacts(prevContacts => {
          const newContacts = prevContacts.filter(contactId => contactId !== contact.contactId);

          if (newContacts.length !== 1) {
            setSelectedChat(null);
          } else {
            setSelectedChat(newContacts[0]);
          }

          return newContacts;
        });

        setPreviousChatMessagesByContact(prev => {
          const updatedMessages = { ...prev };
          delete updatedMessages[contact.contactId];
          return updatedMessages;
        });

        if (onViewContact) onViewContact(undefined, { reason: 'chat-ended', endedContactId: contact.contactId });
      });
    });
  }, [
    chatContactsRef,
    onViewContact,
    selectedChat,
    setActivePage,
    setChatContacts,
    setPreviousChatMessagesByContact,
    setSelectedChat
  ]);

  // Sync selected chat ref
  useEffect(() => {
    if (selectedChat) {
      connect.core.viewContact(selectedChat);
    }
    selectedChatRef.current = selectedChat;
  }, [selectedChat, selectedChatRef]);
}
